import React, {useState} from 'react'
import {Box, Typography} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {ITALIAN_REGIONS} from 'src/constants/mock/county'
import {ITALIAN_PROVINCE} from 'src/constants/mock/province'
import {ITALIAN_CITY} from 'src/constants/mock/city'
import {TimeslotProps} from 'src/types/types'
import CaregiversDialog from './CaregiversDialogNew'
import TimeslotsDetailDialog from './TimeslotsDialogNew'

const WEEK_DAYS: Record<string, string> = {
  MONDAY: 'Lunedì',
  TUESDAY: 'Martedì',
  WEDNESDAY: 'Mercoledì',
  THURSDAY: 'Giovedì',
  FRIDAY: 'Venerdì',
  SATURDAY: 'Sabato',
  SUNDAY: 'Domenica',
}

function formatWeekDay(day?: string): string {
  return day ? WEEK_DAYS[day] ?? day : ''
}

function optionLabel(value: string, options: {value: string; label: string}[]): string | null {
  return options.find(o => o.value === value)?.label ?? null
}

const CONTACT_METHOD_OPTIONS = [
  {value: 'EMAIL', label: 'Email'},
  {value: 'SMS', label: 'SMS'},
  {value: 'PUSH', label: 'Push'},
]

const REMINDER_OPTIONS = [
  {value: 'SMS', label: 'SMS'},
  {value: 'EMAIL', label: 'Email'},
  {value: 'CALL', label: 'Chiamata'},
  {value: 'PUSH', label: 'Push'},
]

const THERAPY_REMINDER_OPTIONS = [
  {value: 'SMS', label: 'SMS'},
  {value: 'EMAIL', label: 'Email'},
  {value: 'VOICE', label: 'Chiamata'},
  {value: 'PUSH', label: 'Push'},
]

type DialogType = 'caregiver' | null

interface ContactDataSectionProps {
  values: Record<string, any>
  setField: (key: string, value: any) => void
  fields: Record<string, any>
  delegate1: Record<string, any> | undefined
  delegate2: Record<string, any> | undefined
  caregiver: Record<string, any> | undefined
  setCaregiver: (v: Record<string, any> | undefined) => void
  timeslots: TimeslotProps[]
  setTimeslots: (v: TimeslotProps[]) => void
}

function personLabel(person: Record<string, any> | undefined): string {
  if (!person) return ''
  return `${person.title || ''} ${person.firstName || ''} ${person.lastName || ''}`.trim()
}

function InfoRow({label, value}: {label: string; value?: string}): JSX.Element {
  return (
    <Box sx={{display: 'flex', gap: 1, alignItems: 'baseline'}}>
      <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500], minWidth: 72, flexShrink: 0}}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{color: value ? 'text.primary' : NEUTRAL_SCALE[300], wordBreak: 'break-word'}}>
        {value || '—'}
      </Typography>
    </Box>
  )
}

function PersonField({
  label,
  person,
  readOnly,
  onSearch,
  onClear,
}: {
  label: string
  person: Record<string, any> | undefined
  readOnly?: boolean
  onSearch?: () => void
  onClear?: () => void
}): JSX.Element {
  const phone = person?.cellPhoneNumber || person?.phoneNumber
  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75}}>
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}
        </Typography>
        {!readOnly && (
          <Box sx={{display: 'flex', gap: 0.5}}>
            <ActionIconButton variant="search" onClick={onSearch} />
            <ActionIconButton variant="close" onClick={onClear} disabled={!person} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          border: `1px solid ${NEUTRAL_SCALE[200]}`,
          borderRadius: '12px',
          p: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          minHeight: PICKER_CARD_MIN_HEIGHT,
          ...(person ? {} : {justifyContent: 'center', alignItems: 'center'}),
        }}
      >
        {person ? (
          <>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
              {personLabel(person)}
            </Typography>
            <InfoRow label="Telefono" value={phone} />
            <InfoRow label="Email" value={person.email} />
          </>
        ) : (
          <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500], textAlign: 'center'}}>
            {'Nessun caregiver selezionato'}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const PICKER_CARD_MIN_HEIGHT = 84

function DelegatePersonField({
  label,
  doctor,
  readOnly,
  onSearch,
  onClear,
}: {
  label: string
  doctor: Record<string, any> | undefined
  readOnly?: boolean
  onSearch?: () => void
  onClear?: () => void
}): JSX.Element {
  const name = doctor
    ? `${doctor.firstName ?? ''} ${doctor.lastName ?? ''}`.trim()
    : ''

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75}}>
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}
        </Typography>
        {!readOnly && (
          <Box sx={{display: 'flex', gap: 0.5}}>
            <ActionIconButton variant="search" onClick={onSearch} />
            <ActionIconButton variant="close" onClick={onClear} disabled={!doctor} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          border: `1px solid ${NEUTRAL_SCALE[200]}`,
          borderRadius: '12px',
          p: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          minHeight: PICKER_CARD_MIN_HEIGHT,
          ...(doctor ? {} : {justifyContent: 'center', alignItems: 'center'}),
        }}
      >
        {doctor ? (
          <>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
              {name}
            </Typography>
            <InfoRow label="Email" value={doctor.email} />
          </>
        ) : (
          <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500], textAlign: 'center'}}>
            {'Nessun delegato selezionato'}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default function ContactDataSection({
  values,
  setField,
  fields,
  delegate1,
  delegate2,
  caregiver,
  setCaregiver,
  timeslots,
  setTimeslots,
}: ContactDataSectionProps): JSX.Element {
  const [dialogType, setDialogType] = useState<DialogType>(null)
  const [openTimeslots, setOpenTimeslots] = useState(false)

  const handleOnSave = (newEl: Record<string, any>) => {
    setCaregiver(newEl)
    setDialogType(null)
  }

  const caregiverElementToFilter = caregiver

  return (
    <Card title="Dati di contatto">
      {/* Fasce orarie preferite */}
      {fields?.preferredTimeSlots && !fields?.preferredTimeSlots?.hidden && (
        <Box sx={{mb: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
            <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
              {'Fasce orarie preferite'}
            </Typography>
            {!fields?.preferredTimeSlots?.readOnly && (
              <ActionIconButton variant="edit" onClick={() => setOpenTimeslots(true)} />
            )}
          </Box>
          {timeslots && timeslots.length > 0 ? (
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
              {timeslots.map(({day, from, to}: TimeslotProps) => (
                <Box
                  key={`${day}-${from}-${to}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    border: `1px solid ${NEUTRAL_SCALE[200]}`,
                    backgroundColor: NEUTRAL_SCALE[50],
                  }}
                >
                  <Typography variant="body2" sx={{color: NEUTRAL_SCALE[900]}}>
                    {formatWeekDay(day)}
                  </Typography>
                  <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500]}}>{'·'}</Typography>
                  <Typography variant="body2" sx={{color: NEUTRAL_SCALE[700]}}>
                    {`${from} → ${to}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500]}}>{'—'}</Typography>
          )}
        </Box>
      )}

      {/* Telefono / Cellulare / Fax / Email / Skype */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
          gap: 2,
          mb: 2,
        }}
      >
        {fields?.phoneNumber && !fields?.phoneNumber?.hidden && (
          fields?.phoneNumber?.readOnly
            ? <ReadOnlyField label="Telefono" value={values.phoneNumber} fullWidth />
            : (
              <TextFieldNew
                label="Telefono"
                value={values.phoneNumber ?? ''}
                onChange={v => setField('phoneNumber', v)}
                fullWidth
              />
            )
        )}
        {fields?.cellPhoneNumber && !fields?.cellPhoneNumber?.hidden && (
          fields?.cellPhoneNumber?.readOnly
            ? <ReadOnlyField label="Cellulare" value={values.cellPhoneNumber} fullWidth />
            : (
              <TextFieldNew
                label="Cellulare"
                value={values.cellPhoneNumber ?? ''}
                onChange={v => setField('cellPhoneNumber', v)}
                fullWidth
              />
            )
        )}
        {fields?.faxNumber && !fields?.faxNumber?.hidden && (
          fields?.faxNumber?.readOnly
            ? <ReadOnlyField label="Fax" value={values.faxNumber} fullWidth />
            : (
              <TextFieldNew
                label="Fax"
                value={values.faxNumber ?? ''}
                onChange={v => setField('faxNumber', v)}
                fullWidth
              />
            )
        )}
        {fields?.email && !fields?.email?.hidden && (
          fields?.email?.readOnly
            ? <ReadOnlyField label="Email" value={values.email} fullWidth />
            : (
              <TextFieldNew
                label="Email"
                value={values.email ?? ''}
                onChange={v => setField('email', v)}
                fullWidth
              />
            )
        )}
        {fields?.skypeId && !fields?.skypeId?.hidden && (
          fields?.skypeId?.readOnly
            ? <ReadOnlyField label="Contatto Skype" value={values.skypeId} fullWidth />
            : (
              <TextFieldNew
                label="Contatto Skype"
                value={values.skypeId ?? ''}
                onChange={v => setField('skypeId', v)}
                fullWidth
              />
            )
        )}
      </Box>

      {/* Indirizzo */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
          gap: 2,
          mb: 2,
        }}
      >
        {fields?.address?.country && !fields?.address?.country?.hidden && (
          <ReadOnlyField label="Stato" value="Italia" fullWidth />
        )}
        {fields?.address?.region && !fields?.address?.region?.hidden && (
          fields?.address?.region?.readOnly
            ? <ReadOnlyField label="Regione" value={optionLabel(values.region, ITALIAN_REGIONS)} fullWidth />
            : (
              <SelectNew
                label="Regione"
                value={values.region ?? ''}
                onChange={v => setField('region', v)}
                options={ITALIAN_REGIONS}
                showSearch
                fullWidth
              />
            )
        )}
        {fields?.address?.province && !fields?.address?.province?.hidden && (
          fields?.address?.province?.readOnly
            ? <ReadOnlyField label="Provincia" value={optionLabel(values.province, ITALIAN_PROVINCE)} fullWidth />
            : (
              <SelectNew
                label="Provincia"
                value={values.province ?? ''}
                onChange={v => setField('province', v)}
                options={ITALIAN_PROVINCE}
                showSearch
                fullWidth
              />
            )
        )}
        {fields?.address?.city && !fields?.address?.city?.hidden && (
          fields?.address?.city?.readOnly
            ? <ReadOnlyField label="Città" value={optionLabel(values.city, ITALIAN_CITY)} fullWidth />
            : (
              <SelectNew
                label="Città"
                value={values.city ?? ''}
                onChange={v => setField('city', v)}
                options={ITALIAN_CITY}
                showSearch
                fullWidth
              />
            )
        )}
        {fields?.address?.address && !fields?.address?.address?.hidden && (
          fields?.address?.address?.readOnly
            ? <ReadOnlyField label="Indirizzo" value={values.address} fullWidth />
            : (
              <TextFieldNew
                label="Indirizzo"
                value={values.address ?? ''}
                onChange={v => setField('address', v)}
                fullWidth
              />
            )
        )}
        {fields?.address?.streetNumber && !fields?.address?.streetNumber?.hidden && (
          fields?.address?.streetNumber?.readOnly
            ? <ReadOnlyField label="Civico" value={values.streetNumber} fullWidth />
            : (
              <TextFieldNew
                label="Civico"
                value={values.streetNumber ?? ''}
                onChange={v => setField('streetNumber', v)}
                fullWidth
              />
            )
        )}
        {fields?.address?.postalCode && !fields?.address?.postalCode?.hidden && (
          fields?.address?.postalCode?.readOnly
            ? <ReadOnlyField label="Codice Postale" value={values.postalCode} fullWidth />
            : (
              <TextFieldNew
                label="Codice Postale"
                value={values.postalCode ?? ''}
                onChange={v => setField('postalCode', v)}
                fullWidth
              />
            )
        )}
        {fields?.address?.locality && !fields?.address?.locality?.hidden && (
          fields?.address?.locality?.readOnly
            ? <ReadOnlyField label="Località" value={values.locality} fullWidth />
            : (
              <TextFieldNew
                label="Località"
                value={values.locality ?? ''}
                onChange={v => setField('locality', v)}
                fullWidth
              />
            )
        )}
      </Box>

      {/* Delegati + Caregiver — stessa riga */}
      {(
        (fields?.delegateId_1 && !fields?.delegateId_1?.hidden) ||
        (fields?.delegateId_2 && !fields?.delegateId_2?.hidden) ||
        (fields?.caregiverId && !fields?.caregiverId?.hidden)
      ) && (
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 2, mb: 2}}>
          {fields?.delegateId_1 && !fields?.delegateId_1?.hidden && (
            <DelegatePersonField
              label="Delegato 1"
              doctor={delegate1}
              readOnly
            />
          )}
          {fields?.delegateId_2 && !fields?.delegateId_2?.hidden && (
            <DelegatePersonField
              label="Delegato 2"
              doctor={delegate2}
              readOnly
            />
          )}
          {fields?.caregiverId && !fields?.caregiverId?.hidden && (
            <PersonField
              label="Caregiver"
              person={caregiver}
              readOnly={fields?.caregiverId?.readOnly}
              onSearch={() => setDialogType('caregiver')}
              onClear={() => setCaregiver(undefined)}
            />
          )}
        </Box>
      )}

      {/* Metodi di comunicazione */}
      {fields?.preferredContacts && !fields?.preferredContacts?.hidden && (
        <Box sx={{mb: 2}}>
          {fields?.preferredContacts?.readOnly ? (
            <ReadOnlyField
              label="Metodi di comunicazione"
              value={[
                values.EMAIL === 'true' ? 'Email' : null,
                values.SMS === 'true' ? 'SMS' : null,
                values.PUSH === 'true' ? 'Push' : null,
              ].filter(Boolean).join(', ') || null}
              fullWidth
            />
          ) : (
            <CheckboxGroupNew
              label="Metodi di comunicazione"
              value={([
                values.EMAIL === 'true' ? 'EMAIL' : null,
                values.SMS === 'true' ? 'SMS' : null,
                values.PUSH === 'true' ? 'PUSH' : null,
              ].filter(Boolean)) as string[]}
              onChange={v => {
                setField('EMAIL', v.includes('EMAIL') ? 'true' : 'false')
                setField('SMS', v.includes('SMS') ? 'true' : 'false')
                setField('PUSH', v.includes('PUSH') ? 'true' : 'false')
              }}
              options={CONTACT_METHOD_OPTIONS}
            />
          )}
        </Box>
      )}

      {/* Remind scadenza Piano Terapeutico */}
      {fields?.remindTherapyExpiryTypes && !fields?.remindTherapyExpiryTypes?.hidden && (
        <Box sx={{mb: 2}}>
          <CheckboxGroupNew
            label="Remind scadenza Piano Terapeutico"
            value={values.remindTherapyExpiryTypes ?? []}
            onChange={v => setField('remindTherapyExpiryTypes', v)}
            options={REMINDER_OPTIONS}
            disabled={fields?.remindTherapyExpiryTypes?.readOnly}
          />
        </Box>
      )}

      {/* Remind assunzione Terapia */}
      {fields?.therapyReminderTypes && !fields?.therapyReminderTypes?.hidden && (
        <Box sx={{mb: 2}}>
          <CheckboxGroupNew
            label="Remind assunzione Terapia"
            value={values.therapyReminderTypes ?? []}
            onChange={v => setField('therapyReminderTypes', v)}
            options={THERAPY_REMINDER_OPTIONS}
            disabled={fields?.therapyReminderTypes?.readOnly}
          />
        </Box>
      )}

      {/* Metodi di contatto */}
      {fields?.preferredContactMethods && !fields?.preferredContactMethods?.hidden && (
        <Box sx={{mb: 2}}>
          <CheckboxGroupNew
            label="Metodi di contatto"
            value={values.preferredContactMethods ?? []}
            onChange={v => setField('preferredContactMethods', v)}
            options={REMINDER_OPTIONS}
            disabled={fields?.preferredContactMethods?.readOnly}
          />
        </Box>
      )}

      {/* Dialogs */}
      {dialogType === 'caregiver' && (
        <CaregiversDialog
          elementToFilter={caregiverElementToFilter}
          onClose={() => setDialogType(null)}
          onSave={handleOnSave}
        />
      )}
      {openTimeslots && (
        <TimeslotsDetailDialog
          onClose={() => setOpenTimeslots(false)}
          onEdit={(newTs: TimeslotProps[]) => { setTimeslots(newTs); setOpenTimeslots(false) }}
          timeslots={timeslots}
          title="Modifica fasce orarie"
        />
      )}
    </Card>
  )
}
